import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sparkles, Plus, MapPin, Compass, Star, Target, Heart,
  Loader2, Lightbulb, RefreshCw, Calendar, Zap, Globe, Lock, Crown
} from "lucide-react";
import { useEntitlements, Features } from "@/hooks/useEntitlements";
import { PaywallScreen, PaywallType } from "@/components/PaywallScreen";

// Currency configuration with fixed exchange rates
const CURRENCIES = {
  USD: { symbol: "$", rate: 1, name: "US Dollar" },
  EUR: { symbol: "€", rate: 0.92, name: "Euro" },
  GBP: { symbol: "£", rate: 0.79, name: "British Pound" },
  CAD: { symbol: "CA$", rate: 1.36, name: "Canadian Dollar" },
  AUD: { symbol: "A$", rate: 1.53, name: "Australian Dollar" },
  JMD: { symbol: "J$", rate: 156.50, name: "Jamaican Dollar" },
};

const CATEGORIES = {
  travel: { icon: MapPin, color: "#E07A5F", bg: "bg-primary/10" },
  adventures: { icon: Compass, color: "#81B29A", bg: "bg-secondary/10" },
  experiences: { icon: Star, color: "#F2CC8F", bg: "bg-accent/20" },
  goals: { icon: Target, color: "#264653", bg: "bg-charcoal/10" },
  romance: { icon: Heart, color: "#EF476F", bg: "bg-coral/10" },
};

const DIFFICULTY_BADGES = {
  easy: { label: "Easy", color: "bg-secondary/20 text-secondary" },
  moderate: { label: "Moderate", color: "bg-accent/30 text-foreground" },
  challenging: { label: "Challenging", color: "bg-primary/20 text-primary" },
};

export const TripSuggestions = ({ isOpen, onClose, onItemAdded }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [seenTitles, setSeenTitles] = useState([]); // Titles previously surfaced — fed to backend as `exclude` so "Get more" never repeats
  const [basedOn, setBasedOn] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addingIndex, setAddingIndex] = useState(null);
  const [aiPowered, setAiPowered] = useState(false);
  const [currency, setCurrency] = useState("USD");
  const [showPaywall, setShowPaywall] = useState(false);
  const [isPremiumLocked, setIsPremiumLocked] = useState(false);
  
  const { hasPaidAccess } = useEntitlements();

  // Load user's currency preference
  const loadCurrency = async () => {
    try {
      const res = await axios.get(`${API}/user/currency`);
      if (res.data.currency) {
        setCurrency(res.data.currency);
      }
    } catch (error) {
      // Use default USD if not logged in or error - expected for non-authenticated users
      console.debug("Currency fetch failed, using default:", error.message);
    }
  };

  const fetchSuggestions = async ({ reset = false } = {}) => {
    setLoading(true);
    setIsPremiumLocked(false);
    try {
      // Build exclude list: every title we've ever shown the user this session,
      // plus any still in view. Reset wipes it (used by initial open).
      const baseExclusions = reset ? [] : seenTitles;
      const stillVisible = suggestions.map((s) => s.title).filter(Boolean);
      const exclude = Array.from(new Set([...baseExclusions, ...stillVisible]))
        .filter(Boolean)
        .slice(0, 60); // safety cap — backend trims to 20

      const params = new URLSearchParams();
      if (exclude.length) params.set("exclude", exclude.join(","));
      // Fresh random seed → backend opens a new LLM session id → fresh output
      params.set("seed", `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);

      const res = await axios.get(`${API}/suggestions/trips?${params.toString()}`);
      const newSuggestions = res.data.suggestions || [];
      setSuggestions(newSuggestions);
      setBasedOn(res.data.based_on);
      setAiPowered(res.data.ai_powered || false);
      // Remember what we've shown so the next refresh can exclude them
      setSeenTitles((prev) => {
        const merged = new Set(reset ? [] : prev);
        newSuggestions.forEach((s) => s.title && merged.add(s.title));
        return Array.from(merged);
      });
    } catch (error) {
      // Check if this is a premium feature lock (403)
      if (error.response?.status === 403) {
        setIsPremiumLocked(true);
        setAiPowered(true); // Show as AI feature since it's premium locked
        // Still show some placeholder/teaser suggestions
        setSuggestions([]);
      } else {
        toast.error("Failed to load suggestions");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadCurrency();
      // Fresh open → reset session memory so the user doesn't see "All caught up"
      // immediately after re-opening
      fetchSuggestions({ reset: true });
    }
  }, [isOpen]);

  const handleCurrencyChange = async (newCurrency) => {
    setCurrency(newCurrency);
    try {
      await axios.put(`${API}/user/currency`, { currency: newCurrency });
    } catch (error) {
      // Silently fail for non-logged in users - expected behavior
      console.debug("Currency save failed:", error.message);
    }
  };

  const handleAddSuggestion = async (index) => {
    setAddingIndex(index);
    try {
      await axios.post(`${API}/suggestions/add/${index}`);
      toast.success("Added to your bucket list!");
      
      // Remove from suggestions
      setSuggestions(suggestions.filter((_, i) => i !== index));
      
      if (onItemAdded) {
        onItemAdded();
      }
    } catch (error) {
      toast.error("Failed to add item");
    } finally {
      setAddingIndex(null);
    }
  };

  const formatCurrency = (amount) => {
    const { rate, symbol } = CURRENCIES[currency];
    const converted = (amount || 0) * rate;
    if (currency === "JMD") {
      return `${symbol}${Math.round(converted).toLocaleString()}`;
    }
    return `${symbol}${converted.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg bg-card border-none rounded-2xl p-0 max-h-[85vh] overflow-hidden">
        <DialogHeader className="p-6 pb-4 border-b border-charcoal/5">
          <DialogTitle className="font-serif text-xl text-charcoal flex items-center gap-2">
            <Lightbulb className="w-5 h-5 text-accent" />
            Trip Suggestions
            {aiPowered && (
              <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-primary/10 text-primary flex items-center gap-1">
                <Zap className="w-3 h-3" />
                AI Powered
              </span>
            )}
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            {aiPowered ? "GPT-5.2 powered ideas based on your bucket list" : "Ideas based on your bucket list preferences"}
          </DialogDescription>
          
          {/* Currency Selector */}
          <div className="flex items-center gap-2 mt-2">
            <Globe className="w-4 h-4 text-muted-foreground" />
            <Select value={currency} onValueChange={handleCurrencyChange}>
              <SelectTrigger className="w-[140px] h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(CURRENCIES).map(([code, { name, symbol }]) => (
                  <SelectItem key={code} value={code}>
                    <span className="flex items-center gap-2">
                      <span className="font-medium">{symbol}</span>
                      <span>{code}</span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </DialogHeader>

        <ScrollArea className="max-h-[65vh]">
          <div className="p-6">
            {loading ? (
              /* Premium skeleton — 3 staggered shimmering cards instead
                 of a generic spinner. Feels curated and alive while the
                 LLM (or pool fetch) returns.  Phase 5 perceived-speed. */
              <div className="space-y-3" data-testid="ai-suggestions-skeleton">
                <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                  <Sparkles className="w-3.5 h-3.5 animate-pulse text-primary" />
                  <span>Finding places you'll love…</span>
                </div>
                {[0, 1, 2].map((idx) => (
                  <div
                    key={idx}
                    className="p-4 rounded-xl bg-card border border-border opacity-0"
                    style={{
                      animation: `duolyst-skeleton-fade 400ms ease-out ${idx * 120}ms forwards`,
                    }}
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-primary/15 to-secondary/15 animate-pulse" />
                      <div className="flex-1 space-y-2">
                        <div className="h-4 w-3/4 bg-muted rounded animate-pulse" />
                        <div className="h-3 w-1/2 bg-muted/70 rounded animate-pulse" />
                        <div className="h-3 w-2/3 bg-muted/50 rounded animate-pulse" />
                      </div>
                    </div>
                  </div>
                ))}
                <style>{`
                  @keyframes duolyst-skeleton-fade {
                    from { opacity: 0; transform: translateY(6px); }
                    to   { opacity: 1; transform: translateY(0); }
                  }
                `}</style>
              </div>
            ) : isPremiumLocked ? (
              /* Premium Locked Teaser State */
              <div className="relative">
                {/* Blurred teaser cards */}
                <div className="space-y-3 blur-sm select-none pointer-events-none">
                  {[1, 2, 3].map((idx) => (
                    <div key={idx} className="p-4 rounded-xl bg-card border border-border">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-lg bg-primary/10" />
                        <div className="flex-1">
                          <div className="h-4 w-3/4 bg-muted rounded" />
                          <div className="h-3 w-1/2 bg-muted rounded mt-2" />
                          <div className="h-3 w-2/3 bg-muted rounded mt-2" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Premium Overlay */}
                <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-t from-background via-background/95 to-background/80 rounded-xl">
                  <div className="text-center p-6">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-amber-500/20 to-orange-500/20 flex items-center justify-center">
                      <Sparkles className="w-8 h-8 text-amber-500" />
                    </div>
                    
                    <h3 className="font-serif text-lg font-semibold mb-2">
                      AI Trip Suggestions
                    </h3>
                    
                    <p className="text-sm text-muted-foreground mb-4 max-w-[250px]">
                      Get personalized trip ideas powered by AI based on your bucket list
                    </p>
                    
                    <Button
                      onClick={() => setShowPaywall(true)}
                      className="bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:opacity-90 rounded-full px-6"
                    >
                      <Lock className="w-4 h-4 mr-2" />
                      Learn more on duolyst.co.uk
                    </Button>
                    
                    <p className="text-xs text-muted-foreground mt-3">
                      Premium feature available on duolyst.co.uk
                    </p>
                  </div>
                </div>
              </div>
            ) : suggestions.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 rounded-full bg-accent/20 flex items-center justify-center mx-auto mb-4">
                  <Sparkles className="w-8 h-8 text-primary" />
                </div>
                <h3 className="font-medium text-charcoal mb-2">All caught up!</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  You've added all our suggestions. Keep exploring!
                </p>
                <Button
                  onClick={fetchSuggestions}
                  variant="outline"
                  className="rounded-full border-charcoal/10"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh Suggestions
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Based on info */}
                {basedOn && (
                  <div className="p-3 bg-accent/10 rounded-xl text-sm text-charcoal flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-primary" />
                    <span>
                      Based on your love for <strong className="capitalize">{basedOn.top_category}</strong> 
                      {" "}({basedOn.total_items} items, {basedOn.completed} completed)
                    </span>
                  </div>
                )}

                {/* Suggestions */}
                {suggestions.map((suggestion, index) => {
                  const category = CATEGORIES[suggestion.category] || CATEGORIES.experiences;
                  const CategoryIcon = category.icon;
                  const difficulty = DIFFICULTY_BADGES[suggestion.difficulty] || DIFFICULTY_BADGES.moderate;
                  
                  return (
                    <div
                      key={suggestion.id || suggestion.title || `suggestion-${index}`}
                      className="p-4 bg-background rounded-xl border border-charcoal/5 hover:border-charcoal/10 transition-colors"
                      data-testid={`suggestion-${index}`}
                    >
                      <div className="flex items-start gap-4">
                        <div
                          className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${category.bg}`}
                        >
                          <CategoryIcon className="w-6 h-6" style={{ color: category.color }} />
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-medium text-charcoal">{suggestion.title}</h3>
                          </div>
                          
                          <p className="text-sm text-muted-foreground mb-2">
                            {suggestion.description}
                          </p>

                          {/* AI-powered additional info */}
                          {(suggestion.estimated_cost || suggestion.duration_days || suggestion.best_season) && (
                            <div className="flex flex-wrap gap-2 mb-3 text-xs text-muted-foreground">
                              {suggestion.estimated_cost && (
                                <span className="flex items-center gap-1 bg-card px-2 py-1 rounded">
                                  {formatCurrency(suggestion.estimated_cost)}
                                </span>
                              )}
                              {suggestion.duration_days && (
                                <span className="flex items-center gap-1 bg-card px-2 py-1 rounded">
                                  <Calendar className="w-3 h-3" />
                                  {suggestion.duration_days} days
                                </span>
                              )}
                              {suggestion.best_season && suggestion.best_season !== "any" && (
                                <span className="bg-card px-2 py-1 rounded capitalize">
                                  Best in {suggestion.best_season}
                                </span>
                              )}
                            </div>
                          )}

                          {/* Why recommended (AI) */}
                          {suggestion.why_recommended && (
                            <p className="text-xs text-primary/80 italic mb-3">
                              "{suggestion.why_recommended}"
                            </p>
                          )}
                          
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span 
                                className="text-xs px-2 py-0.5 rounded-full capitalize"
                                style={{ background: `${category.color}15`, color: category.color }}
                              >
                                {suggestion.category}
                              </span>
                              <span className={`text-xs px-2 py-0.5 rounded-full ${difficulty.color}`}>
                                {difficulty.label}
                              </span>
                            </div>
                            
                            <Button
                              size="sm"
                              onClick={() => handleAddSuggestion(index)}
                              disabled={addingIndex === index}
                              className="bg-primary text-white hover:bg-primary/90 rounded-full h-8 px-4"
                              data-testid={`add-suggestion-${index}`}
                            >
                              {addingIndex === index ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <>
                                  <Plus className="w-4 h-4 mr-1" />
                                  Add
                                </>
                              )}
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}

                {/* Refresh button */}
                <div className="text-center pt-4">
                  <Button
                    onClick={fetchSuggestions}
                    variant="ghost"
                    className="text-muted-foreground hover:text-charcoal"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Get more suggestions
                  </Button>
                </div>
              </div>
            )}
          </div>
        </ScrollArea>
      </DialogContent>
      
      {/* Premium Paywall */}
      <PaywallScreen
        isOpen={showPaywall}
        onClose={() => setShowPaywall(false)}
        type={PaywallType.TRIP_SUGGESTIONS}
        triggerSource="ai_suggestions"
      />
    </Dialog>
  );
};

export default TripSuggestions;
